import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';

/** At most one refresh per this long. */
export const PROFILE_REFRESH_INTERVAL_MS = 5 * 60_000;

/**
 * Re-read the signed-in profile (GET /auth/me, through the existing
 * refreshProfile) when the browser tab comes back into view - so a
 * permission granted or revoked while the person was away (for example
 * lottery.upload, which decides the Lottery sidebar section) shows without
 * signing in again.
 *
 * Safe by construction:
 *   * at most ONCE per PROFILE_REFRESH_INTERVAL_MS - `focus` and
 *     `visibilitychange` usually fire together, and a fast tab-switcher
 *     must not hammer the API. The clock starts when the layout mounts
 *     (the profile was just loaded at sign-in or page load);
 *   * never two at once (an in-flight guard);
 *   * no loop: the effect depends only on isAuthenticated and the stable
 *     refreshProfile - NOT on `user`, which the refresh itself updates;
 *   * failures are swallowed: the cached profile stays, nothing is shown.
 *     (A dead session is still handled by the API client's normal 401 path.)
 */
export function useProfileRefreshOnFocus(): void {
  const { isAuthenticated, refreshProfile } = useAuth();
  const lastRefreshAt = useRef(Date.now());
  const inFlight = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    const maybeRefresh = () => {
      if (document.visibilityState === 'hidden') return;
      if (inFlight.current) return;
      const now = Date.now();
      if (now - lastRefreshAt.current < PROFILE_REFRESH_INTERVAL_MS) return;

      lastRefreshAt.current = now; // counts even if it fails: no retry storm
      inFlight.current = true;
      refreshProfile()
        .catch(() => undefined)
        .finally(() => {
          inFlight.current = false;
        });
    };

    window.addEventListener('focus', maybeRefresh);
    document.addEventListener('visibilitychange', maybeRefresh);
    return () => {
      window.removeEventListener('focus', maybeRefresh);
      document.removeEventListener('visibilitychange', maybeRefresh);
    };
  }, [isAuthenticated, refreshProfile]);
}
